import { MONTH_LONG, addDays, monthKey } from './dates';
import { compareNames } from './members';
import { isActiveMember } from './membership';
import type { Member } from './types';

/**
 * Aniversariantes (Membresia): quem faz aniversário num mês ou nos próximos dias, pela data
 * de nascimento do cadastro. Só os membros ativos entram — o inativo e o anonimizado (que
 * perde a data) ficam de fora. Quem nasceu em 29 de fevereiro comemora em 28 nos anos que
 * não são bissextos.
 */

/** Quantos dias o "próximos aniversários" olha à frente (hoje incluído). */
export const BIRTHDAY_WINDOW_DAYS = 7;

export interface Birthday {
  member: Member;
  /** A data do aniversário no ano em questão (ISO). */
  date: string;
  /** A idade que completa nessa data; `null` sem o ano de nascimento válido. */
  turning: number | null;
}

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

const isLeap = (year: number) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

/** O aniversário de quem nasceu em `birthDate` no ano `year` (29/02 vira 28/02 fora do bissexto). */
export function birthdayIn(birthDate: string | null, year: number): string | null {
  const match = birthDate ? ISO.exec(birthDate) : null;

  if (!match) return null;

  const [, , month = '', day = ''] = match;
  const date = month === '02' && day === '29' && !isLeap(year) ? '28' : day;

  return `${year}-${month}-${date}`;
}

function birthdayOf(member: Member, year: number): Birthday | null {
  const date = birthdayIn(member.birthDate, year);

  if (!date || !member.birthDate) return null;

  const turning = year - Number(member.birthDate.slice(0, 4));

  return { member, date, turning: turning > 0 ? turning : null };
}

const byDate = (a: Birthday, b: Birthday) =>
  a.date.localeCompare(b.date) || compareNames(a.member.name, b.member.name);

/** Os aniversários dos membros ativos entre `from` e `to` (inclusive), mesmo virando o ano. */
export function birthdaysBetween(members: readonly Member[], from: string, to: string): Birthday[] {
  if (to < from) return [];

  const years: number[] = [];

  for (let y = Number(from.slice(0, 4)); y <= Number(to.slice(0, 4)); y++) years.push(y);

  return members
    .filter(isActiveMember)
    .flatMap((m) => years.map((y) => birthdayOf(m, y)))
    .filter((b): b is Birthday => !!b && b.date >= from && b.date <= to)
    .sort(byDate);
}

/** Os aniversários de um mês (`"2026-11"`). */
export function birthdaysInMonth(members: readonly Member[], month: string): Birthday[] {
  const [year = 0, m = 1] = month.split('-').map(Number);
  const last = new Date(Date.UTC(year, m, 0)).getUTCDate();

  return birthdaysBetween(members, `${month}-01`, `${month}-${String(last).padStart(2, '0')}`);
}

/** Os próximos aniversários, de hoje até `days - 1` dias depois. */
export function upcomingBirthdays(
  members: readonly Member[],
  today: string,
  days: number = BIRTHDAY_WINDOW_DAYS
): Birthday[] {
  return birthdaysBetween(members, today, addDays(today, days - 1));
}

/** O mês vizinho de `"2026-11"`: `shiftMonth("2026-11", 1)` = `"2026-12"`. */
export function shiftMonth(month: string, delta: number): string {
  const [year = 0, m = 1] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year, m - 1 + delta, 1));

  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** "novembro de 2026". */
export function monthLabel(month: string): string {
  const [year = 0, m = 1] = month.split('-').map(Number);

  return `${MONTH_LONG[m - 1] ?? ''} de ${year}`;
}

/** Os números do topo: hoje, os próximos dias, o mês e quantos ativos não têm a data. */
export function birthdayNumbers(members: readonly Member[], today: string, month: string = monthKey(today)) {
  const active = members.filter(isActiveMember);

  return {
    today: birthdaysBetween(active, today, today).length,
    upcoming: upcomingBirthdays(active, today).length,
    month: birthdaysInMonth(active, month).length,
    missing: active.filter((m) => !birthdayIn(m.birthDate, 2000)).length,
  };
}

/** Como a pessoa é chamada: o nome de apresentação ou o primeiro nome. */
function callName(member: Member): string {
  return member.nickname.trim() || member.name.trim().split(/\s+/)[0] || member.name;
}

/** A mensagem de parabéns pronta para o WhatsApp (editável no próprio WhatsApp). */
export function birthdayMessage(member: Member, orgName: string): string {
  return [
    `Feliz aniversário, ${callName(member)}! 🎉`,
    '',
    `A família ${orgName} celebra a sua vida hoje e agradece a Deus por você estar com a gente. Que este novo ano seja cheio de alegria, saúde e bênçãos!`,
  ].join('\n');
}

/** A lista do mês em texto, para colar no grupo da igreja ou no mural: "03/11 · Ana Silva". */
export function birthdayListText(birthdays: readonly Birthday[], month: string, orgName: string): string {
  const lines = birthdays.map((b) => `${b.date.slice(8)}/${b.date.slice(5, 7)} · ${b.member.name}`);

  return [
    `🎂 Aniversariantes de ${monthLabel(month)} — ${orgName}`,
    '',
    ...(lines.length ? lines : ['Nenhum aniversariante neste mês.']),
  ].join('\n');
}
